// src/pages/Surveys.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { surveyService } from '../services/surveyService';
import { companyService } from '../services/companyService';
import './Surveys.css';

// Types de questions disponibles
const QUESTION_TYPES = [
  { id: 'rating', name: 'Évaluation (étoiles)', icon: '⭐' },
  { id: 'choice', name: 'Choix unique', icon: '🔘' },
  { id: 'multiple', name: 'Choix multiples', icon: '☑️' },
  { id: 'text', name: 'Texte court', icon: '📝' },
  { id: 'textarea', name: 'Texte long', icon: '📄' },
  { id: 'boolean', name: 'Oui/Non', icon: '✅' },
  { id: 'number', name: 'Nombre', icon: '🔢' },
];

// Modal de création/modification d'enquête
const SurveyFormModal = ({ survey, companies = [], onSave, onClose }) => {
  const isEditing = !!survey;
  
  const [formData, setFormData] = useState(isEditing ? {
    title: survey.title || '',
    description: survey.description || '',
    questions: survey.questions || [],
    isActive: survey.isActive !== undefined ? survey.isActive : true,
    targetCompanies: survey.targetCompanies || [],
    startDate: survey.startDate ? new Date(survey.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    endDate: survey.endDate ? new Date(survey.endDate).toISOString().split('T')[0] : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  } : {
    title: '',
    description: '',
    questions: [],
    isActive: true,
    targetCompanies: [],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, isEditing ? survey._id : null);
  };

  const addQuestion = (question) => {
    if (editingQuestion) {
      setFormData({
        ...formData,
        questions: formData.questions.map(q => 
          q.id === editingQuestion.id ? { ...question, id: editingQuestion.id } : q
        )
      });
    } else {
      setFormData({
        ...formData,
        questions: [...formData.questions, { ...question, id: Date.now().toString() }]
      });
    }
    setEditingQuestion(null);
    setShowQuestionModal(false);
  };

  const removeQuestion = (questionId) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter(q => q.id !== questionId)
    });
  };

  const editQuestion = (question) => {
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? '✏️ Modifier l\'enquête' : '📊 Créer une nouvelle enquête'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="survey-form">
          <div className="modal-body form-body">
            {/* Informations générales */}
            <div className="form-section">
              <h3>Informations générales</h3>
              <div className="form-group">
                <label>Titre de l'enquête *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Ex: Enquête de satisfaction 2026"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  placeholder="Décrivez l'objectif de cette enquête..."
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de début *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date de fin *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    Enquête active
                  </label>
                </div>
              </div>

              {/* Sélection des entreprises ciblées */}
              <div className="form-group">
                <label>Entreprises ciblées</label>
                <select
                  multiple
                  value={formData.targetCompanies}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
                    setFormData({...formData, targetCompanies: selectedOptions});
                  }}
                  size="4"
                  style={{width: '100%', padding: '8px', borderRadius: '4px'}}
                >
                  <option value="">Toutes les entreprises</option>
                  {Array.isArray(companies) && companies.length > 0 ? (
                    companies.map(company => (
                      <option key={company._id} value={company._id}>
                        🏢 {company.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Aucune entreprise disponible</option>
                  )}
                </select>
                <small style={{color: '#666', fontSize: '12px', marginTop: '4px'}}>
                  Laissez vide pour cibler toutes les entreprises. Maintenez Ctrl (Cmd) pour sélectionner plusieurs.
                </small>
                
                {formData.targetCompanies.length > 0 && (
                  <div style={{marginTop: '10px'}}>
                    <strong>Entreprises sélectionnées ({formData.targetCompanies.length}):</strong>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px'}}>
                      {formData.targetCompanies.map(companyId => {
                        const company = companies.find(c => c._id === companyId);
                        return (
                          <span 
                            key={companyId} 
                            style={{
                              background: '#e0e7ff',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {company?.name || companyId}
                            <button 
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData, 
                                  targetCompanies: formData.targetCompanies.filter(id => id !== companyId)
                                });
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ✕
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section des questions */}
            <div className="form-section">
              <div className="section-header">
                <h3>Questions de l'enquête</h3>
                <Button 
                  type="button"
                  variant="primary" 
                  size="sm"
                  onClick={() => {
                    setEditingQuestion(null);
                    setShowQuestionModal(true);
                  }}
                >
                  ➕ Ajouter une question
                </Button>
              </div>

              {formData.questions.length === 0 ? (
                <div className="empty-questions">
                  <p>Aucune question pour le moment</p>
                  <small>Cliquez sur "Ajouter une question" pour commencer</small>
                </div>
              ) : (
                <div className="questions-list">
                  {formData.questions.map((question, index) => {
                    const typeInfo = QUESTION_TYPES.find(t => t.id === question.type);
                    return (
                      <div key={question.id} className="question-item">
                        <div className="question-header">
                          <div className="question-number">Q{index + 1}</div>
                          <div className="question-content">
                            <div className="question-text">
                              {question.text}
                              {question.required && <span className="required-badge">Obligatoire</span>}
                            </div>
                            <div className="question-type">
                              <span className="type-badge">
                                {typeInfo?.icon} {typeInfo?.name}
                              </span>
                            </div>
                          </div>
                          <div className="question-actions">
                            <button 
                              type="button"
                              className="icon-btn edit"
                              onClick={() => editQuestion(question)}
                            >
                              ✏️
                            </button>
                            <button 
                              type="button"
                              className="icon-btn delete"
                              onClick={() => removeQuestion(question.id)}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        {question.options && question.options.length > 0 && (
                          <div className="question-options">
                            <strong>Options:</strong>
                            <div className="options-list">
                              {question.options.map((opt, i) => (
                                <span key={i} className="option-tag">{opt}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer form-footer">
            <Button type="button" variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" variant="primary">
              {isEditing ? '💾 Enregistrer les modifications' : '📊 Créer l\'enquête'}
            </Button>
          </div>
        </form>
      </div>

      {/* Modal d'ajout/modification de question */}
      {showQuestionModal && (
        <QuestionFormModal
          question={editingQuestion}
          onSave={addQuestion}
          onClose={() => {
            setShowQuestionModal(false);
            setEditingQuestion(null);
          }}
        />
      )}
    </div>
  );
};

// Modal pour ajouter/modifier une question
const QuestionFormModal = ({ question, onSave, onClose }) => {
  const isEditing = !!question;
  
  const [formData, setFormData] = useState(isEditing ? {
    text: question.text || '',
    type: question.type || 'text',
    required: question.required || false,
    options: question.options ? question.options.join('\n') : '',
    min: question.min || 1,
    max: question.max || 5,
    placeholder: question.placeholder || ''
  } : {
    text: '',
    type: 'text',
    required: false,
    options: '',
    min: 1,
    max: 5,
    placeholder: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const questionData = {
      id: question?.id || Date.now().toString(),
      text: formData.text,
      type: formData.type,
      required: formData.required,
    };
    
    // Ajouter les champs spécifiques selon le type
    if (formData.type === 'choice' || formData.type === 'multiple') {
      questionData.options = formData.options.split('\n').filter(opt => opt.trim() !== '');
    }
    
    if (formData.type === 'rating') {
      questionData.min = parseInt(formData.min) || 1;
      questionData.max = parseInt(formData.max) || 5;
    }
    
    if (formData.type === 'text' || formData.type === 'textarea') {
      questionData.placeholder = formData.placeholder;
    }
    
    onSave(questionData);
  };

  const needsOptions = formData.type === 'choice' || formData.type === 'multiple';
  const needsRating = formData.type === 'rating';
  const needsPlaceholder = formData.type === 'text' || formData.type === 'textarea';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? '✏️ Modifier la question' : '➕ Ajouter une question'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Texte de la question *</label>
              <input
                type="text"
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                required
                placeholder="Ex: Comment évaluez-vous l'application ?"
              />
            </div>

            <div className="form-group">
              <label>Type de réponse *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                required
              >
                {QUESTION_TYPES.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.icon} {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData({...formData, required: e.target.checked})}
                />
                Question obligatoire
              </label>
            </div>

            {needsOptions && (
              <div className="form-group">
                <label>Options (une par ligne)</label>
                <textarea
                  value={formData.options}
                  onChange={(e) => setFormData({...formData, options: e.target.value})}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows="4"
                  required
                />
              </div>
            )}

            {needsRating && (
              <div className="form-row">
                <div className="form-group">
                  <label>Valeur minimum</label>
                  <input
                    type="number"
                    value={formData.min}
                    onChange={(e) => setFormData({...formData, min: e.target.value})}
                    min="0"
                    max="10"
                  />
                </div>
                <div className="form-group">
                  <label>Valeur maximum</label>
                  <input
                    type="number"
                    value={formData.max}
                    onChange={(e) => setFormData({...formData, max: e.target.value})}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            )}

            {needsPlaceholder && (
              <div className="form-group">
                <label>Placeholder (optionnel)</label>
                <input
                  type="text"
                  value={formData.placeholder}
                  onChange={(e) => setFormData({...formData, placeholder: e.target.value})}
                  placeholder="Ex: Votre réponse..."
                />
              </div>
            )}
          </div>

          <div className="modal-footer">
            <Button type="button" variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" variant="primary">
              {isEditing ? '💾 Enregistrer' : '➕ Ajouter'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal pour voir les réponses
// Remplace le ResponsesModal existant par celui-ci
// Modal pour voir les réponses - Version améliorée
const ResponsesModal = ({ survey, responses, onClose, onExport }) => {
  const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'table'
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Réponses - {survey?.title}</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Boutons de vue */}
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Vue cartes"
              >
                📇 Cartes
              </button>
              <button 
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Vue tableau"
              >
                📋 Tableau
              </button>
            </div>
            {/* Bouton export CSV */}
            <Button 
              variant="mint" 
              size="sm"
              onClick={() => onExport(survey, responses)}
            >
              📥 Exporter CSV
            </Button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-body">
          <div className="responses-stats">
            <div className="stat-item">
              <div className="stat-number">{responses.length}</div>
              <div className="stat-label">Total réponses</div>
            </div>
          </div>

          {responses.length === 0 ? (
            <div className="empty-responses">
              <div className="empty-icon">📊</div>
              <p>Aucune réponse pour le moment</p>
            </div>
          ) : viewMode === 'table' ? (
            // VUE TABLEAU
            <div className="responses-table-container">
              <table className="responses-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Email</th>
                    <th>ID Employé</th>
                    <th>Date</th>
                    {survey?.questions?.map((q, idx) => (
                      <th key={q.id} title={q.text}>Q{idx + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {responses.map((response, idx) => {
                    // Extraire nom et prénom
                    let firstName = '';
                    let lastName = '';
                    let fullName = response.fullName || '';
                    let email = response.email || '';
                    let employeeId = response.employeeId || response.userId || '-';
                    
                    if (response.user) {
                      firstName = response.user.firstName || '';
                      lastName = response.user.lastName || '';
                      email = response.user.email || email;
                      employeeId = response.user.employeeId || employeeId;
                    } else if (fullName) {
                      const nameParts = fullName.split(' ');
                      firstName = nameParts[0] || '';
                      lastName = nameParts.slice(1).join(' ') || '';
                    }
                    
                    return (
                      <tr key={idx}>
                        <td data-label="Nom">{firstName || '-'}</td>
                        <td data-label="Prénom">{lastName || '-'}</td>
                        <td data-label="Email">{email || '-'}</td>
                        <td data-label="ID Employé">{employeeId}</td>
                        <td data-label="Date">
                          {new Date(response.submittedAt).toLocaleDateString('fr-FR')}
                        </td>
                        {survey?.questions?.map(question => {
                          const answer = response.answers?.find(a => a.questionId === question.id);
                          let answerValue = '-';
                          if (answer) {
                            if (Array.isArray(answer.answer)) {
                              answerValue = answer.answer.join(', ');
                            } else if (typeof answer.answer === 'boolean') {
                              answerValue = answer.answer ? '✅ Oui' : '❌ Non';
                            } else {
                              answerValue = answer.answer?.toString() || '-';
                            }
                          }
                          return <td key={question.id} data-label={question.text.substring(0, 20)}>{answerValue}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            // VUE CARTES (originale)
            <div className="responses-list">
              {responses.map((response, idx) => {
                // Extraire nom et prénom
                let firstName = '';
                let lastName = '';
                let fullName = response.fullName || '';
                let email = response.email || '';
                let employeeId = response.employeeId || response.userId || '-';
                
                if (response.user) {
                  firstName = response.user.firstName || '';
                  lastName = response.user.lastName || '';
                  email = response.user.email || email;
                  employeeId = response.user.employeeId || employeeId;
                } else if (fullName) {
                  const nameParts = fullName.split(' ');
                  firstName = nameParts[0] || '';
                  lastName = nameParts.slice(1).join(' ') || '';
                }
                
                return (
                  <Card key={idx} className="response-card" padding="md">
                    <div className="response-header">
                      <div className="response-user">
                        <strong>👤 {firstName} {lastName}</strong>
                        {employeeId && <span className="response-employee-id">({employeeId})</span>}
                      </div>
                      <div className="response-date">
                        {new Date(response.submittedAt).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <div className="response-contact">
                      {email && <span className="response-email">📧 {email}</span>}
                    </div>
                    <div className="response-answers">
                      {response.answers?.map((answer, aidx) => {
                        const question = survey?.questions?.find(q => q.id === answer.questionId);
                        return (
                          <div key={aidx} className="response-answer">
                            <div className="answer-question">{question?.text || answer.questionId}</div>
                            <div className="answer-value">
                              {Array.isArray(answer.answer) 
                                ? answer.answer.join(', ') 
                                : typeof answer.answer === 'boolean'
                                  ? (answer.answer ? '✅ Oui' : '❌ Non')
                                  : answer.answer}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="primary" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant principal
const Surveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [showResponses, setShowResponses] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');





  // Ajoute cette fonction dans le composant Surveys, avant le return

// Exporter les réponses en CSV
// Exporter les réponses en CSV - Version avec tableau complet
const exportResponsesToCSV = (survey, responses) => {
  if (!responses || responses.length === 0) {
    alert('Aucune réponse à exporter');
    return;
  }

  // 1. Construire l'en-tête du CSV avec colonnes utilisateur
  const headers = ['Nom', 'Prénom', 'Email', 'ID Employé', 'Date soumission'];
  
  // Ajouter les questions comme en-têtes
  survey.questions?.forEach((q, idx) => {
    headers.push(`Q${idx + 1}: ${q.text}`);
  });

  // 2. Construire les lignes
  const rows = responses.map(response => {
    // Extraire nom et prénom
    let firstName = '';
    let lastName = '';
    let fullName = response.fullName || '';
    let email = response.email || '';
    let employeeId = response.employeeId || response.userId || '-';
    let submittedAt = response.submittedAt ? new Date(response.submittedAt).toLocaleString('fr-FR') : '-';
    
    // Extraire depuis l'objet user si disponible
    if (response.user) {
      firstName = response.user.firstName || '';
      lastName = response.user.lastName || '';
      email = response.user.email || email;
      employeeId = response.user.employeeId || employeeId;
      fullName = `${firstName} ${lastName}`.trim();
    } else if (fullName) {
      const nameParts = fullName.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }
    
    const row = [
      firstName || '-',
      lastName || '-',
      email || '-',
      employeeId,
      submittedAt
    ];
    
    // Ajouter les réponses pour chaque question
    survey.questions?.forEach(question => {
      const answer = response.answers?.find(a => a.questionId === question.id);
      let answerValue = '-';
      if (answer) {
        if (Array.isArray(answer.answer)) {
          answerValue = answer.answer.join('; ');
        } else if (typeof answer.answer === 'boolean') {
          answerValue = answer.answer ? 'Oui' : 'Non';
        } else {
          answerValue = answer.answer?.toString() || '-';
        }
      }
      row.push(answerValue);
    });
    
    return row;
  });

  // 3. Créer le contenu CSV
  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  // 4. Ajouter BOM pour UTF-8
  const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // 5. Télécharger le fichier
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const fileName = `enquete_${survey.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};




  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await surveyService.getSurveys();
      setSurveys(data || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des enquêtes');
    } finally {
      setLoading(false);
    }
  };



  const fetchCompanies = async () => {
    try {
      const response = await companyService.getCompanies();
      let companiesData = [];
      if (Array.isArray(response)) companiesData = response;
      else if (response?.data && Array.isArray(response.data)) companiesData = response.data;
      else if (response?.companies && Array.isArray(response.companies)) companiesData = response.companies;
      setCompanies(companiesData);
    } catch (err) {
      console.error('Erreur chargement entreprises:', err);
    }
  };

  useEffect(() => {
    fetchSurveys();
    fetchCompanies();
  }, []);

  const handleCreateSurvey = async (surveyData) => {
    try {
      await surveyService.createSurvey(surveyData);
      setShowForm(false);
      await fetchSurveys();
      alert('✅ Enquête créée avec succès !');
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
    }
  };

  const handleUpdateSurvey = async (surveyData, surveyId) => {
    try {
      await surveyService.updateSurvey(surveyId, surveyData);
      setEditingSurvey(null);
      await fetchSurveys();
      alert('✅ Enquête modifiée avec succès !');
    } catch (err) {
      setError(err.message || 'Erreur lors de la modification');
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette enquête ?')) return;
    try {
      await surveyService.deleteSurvey(surveyId);
      await fetchSurveys();
      alert('✅ Enquête supprimée !');
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleViewResponses = async (survey) => {
    try {
      const responses = await surveyService.getSurveyResponses(survey._id);
      setShowResponses({ survey, responses: responses || [] });
    } catch (err) {
      alert('Erreur lors du chargement des réponses');
    }
  };

  const filteredSurveys = surveys.filter(survey => {
    if (activeFilter === 'active') return survey.isActive;
    if (activeFilter === 'inactive') return !survey.isActive;
    return true;
  });

  if (loading) {
    return (
      <div className="surveys">
        <div className="page-header">
          <h1>Gestion des Enquêtes</h1>
          <p>Chargement...</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="surveys">
      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Enquêtes</h1>
          <p>Créez et gérez vos enquêtes de satisfaction ({surveys.length} enquête{surveys.length > 1 ? 's' : ''})</p>
        </div>
        <div className="header-actions">
          <div className="filters">
            <select 
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Toutes les enquêtes</option>
              <option value="active">🟢 Actives</option>
              <option value="inactive">🔴 Inactives</option>
            </select>
            <Button variant="primary" onClick={() => setShowForm(true)}>
              📊 Nouvelle enquête
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      <div className="surveys-stats">
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-number">{surveys.length}</div>
              <div className="stat-label">Total enquêtes</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">🟢</div>
            <div className="stat-info">
              <div className="stat-number">{surveys.filter(s => s.isActive).length}</div>
              <div className="stat-label">Enquêtes actives</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">🏢</div>
            <div className="stat-info">
              <div className="stat-number">{surveys.filter(s => s.targetCompanies?.length > 0).length}</div>
              <div className="stat-label">Ciblées par entreprise</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="surveys-grid">
        {filteredSurveys.map(survey => (
          <Card key={survey._id} className="survey-card" padding="lg">
            <div className="survey-header">
              <div className="survey-icon">📊</div>
              <div className="survey-info">
                <h3 className="survey-title">{survey.title}</h3>
                <div className="survey-meta">
                  <span className={`status-badge ${survey.isActive ? 'active' : 'inactive'}`}>
                    {survey.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                  <span className="questions-count">📝 {survey.questions?.length || 0} questions</span>
                </div>
              </div>
            </div>

            <p className="survey-description">{survey.description}</p>

            <div className="survey-dates">
              <div className="date-item">
                <span className="date-label">Début:</span>
                <span>{new Date(survey.startDate).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="date-item">
                <span className="date-label">Fin:</span>
                <span>{new Date(survey.endDate).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>

            {survey.targetCompanies && survey.targetCompanies.length > 0 && (
              <div className="target-companies">
                <strong>Entreprises ciblées:</strong>
                <div className="companies-tags">
                  {survey.targetCompanies.map(companyId => {
                    const company = companies.find(c => c._id === companyId);
                    return (
                      <span key={companyId} className="company-tag">
                        🏢 {company?.name || companyId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="survey-actions">
              <Button variant="secondary" size="sm" onClick={() => handleViewResponses(survey)}>
                📊 Voir réponses
              </Button>
              <Button variant="primary" size="sm" onClick={() => setEditingSurvey(survey)}>
                ✏️ Modifier
              </Button>
              <Button variant="danger" size="sm" onClick={() => handleDeleteSurvey(survey._id)}>
                🗑️ Supprimer
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {filteredSurveys.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>Aucune enquête trouvée</h3>
          <Button variant="primary" onClick={() => setShowForm(true)}>
            📊 Créer une enquête
          </Button>
        </Card>
      )}

      {showForm && (
        <SurveyFormModal
          companies={companies}
          onSave={handleCreateSurvey}
          onClose={() => setShowForm(false)}
        />
      )}

      {editingSurvey && (
        <SurveyFormModal
          survey={editingSurvey}
          companies={companies}
          onSave={handleUpdateSurvey}
          onClose={() => setEditingSurvey(null)}
        />
      )}

      {showResponses && (
        <ResponsesModal
            survey={showResponses.survey}
            responses={showResponses.responses}
            onExport={exportResponsesToCSV}
            onClose={() => setShowResponses(null)}
        />
        )}
    </div>
  );
};

export default Surveys;